<template>
  <transition
    :name="name"
    @after-enter="handleAfterEnter"
    @enter="handleEnter"
    @leave="handleLeave"
  >
    <slot />
  </transition>
</template>

<script>

/**
 * Vue: Transition height (transition-height)
 * -----------------------------------------------------------------------------
 * Smoothly transition an element's height between `0` and `auto`.
 * @params {String} name - Transition name
 */

import { mapState } from 'vuex'

export default {
  name: 'TransitionHeight',

  props: {
    name: {
      type: String,
      default: 'height',
    },
  },

  data() {
    return {
      parentElement: null,
    }
  },

  computed: {

    /**
     * Map Vuex state
     */
    ...mapState({
      screenWidth: (state) => state.index.screen.width,
    }),
  },

  watch: {
    screenWidth() {
      this.handleResize()
    },
  },

  methods: {

    /**
     * Reset element height after the enter animation.
     * @param {Element} element - target element.
     */
    handleAfterEnter(element) {
      element.style.height = 'auto'
    },

    /**
     * Set element height to an exact value when the enter animation begins.
     * @param {Element} element - target element.
     */
    handleEnter(element) {
      const { parentElement } = element

      if (parentElement) {
        this.parentElement = parentElement
        element.style.maxWidth = 'var(--accordion-width)'
        parentElement.style.setProperty(
          '--accordion-width',
          getComputedStyle(parentElement).width,
        )
      } else {
        element.style.width = null
        element.style.width = getComputedStyle(element).width
      }

      /**
       * Calculate the element's height when the property is set to `auto`.
       */
      element.style.height = 'auto'
      element.style.position = 'absolute'
      element.style.visibility = 'hidden'

      const targetHeight = getComputedStyle(element).height

      element.style.height = 0
      element.style.position = null
      element.style.visibility = null

      /**
       * Force repaint to ensure the animation triggers correctly.
       */
      // eslint-disable-next-line no-unused-expressions
      getComputedStyle(element).height

      /**
       * Set the element's height to begin the transition.
       * - Using `requestAnimationFrame` ensures that the transition will wait
       *   until the starting height is zero and not `auto`.
       */
      requestAnimationFrame(() => {
        element.style.height = targetHeight
      })
    },

    /**
     * Set element height to zero when leave animation begins.
     * @param {Element} element - target element.
     */
    handleLeave(element) {

      /**
       * Set the element's height property to its current height.
       */
      element.style.height = getComputedStyle(element).height

      /**
       * Force repaint to ensure the animation triggers correctly.
       */
      // eslint-disable-next-line no-unused-expressions
      getComputedStyle(element).height

      /**
       * Set the element's height to zero to begin the transition.
       * - Using `requestAnimationFrame` ensures that the transition will wait
       *   until the starting height is a fixed value and not set to `auto`.
       */
      requestAnimationFrame(() => {
        element.style.height = 0
      })
    },

    /**
     * Workaround for when content on Safari is ignoring parent width and
     * overflowing
     */
    async handleResize() {
      if (!this.parentElement) {
        return
      }
      await this.$nextTick()
      const width = getComputedStyle(this.parentElement).width
      this.parentElement.style.setProperty('--accordion-width', width)
    },
  },
}
</script>

<style lang="scss">
@import './transition-height';
</style>
